import React, { useEffect, useMemo, useState } from "react";
import { FaCheck, FaPhone, FaWhatsapp } from "react-icons/fa6";
import { RiArrowDownSLine } from "react-icons/ri";
import whyChooseUsImage from "@/assets/pricing/whyChooseUs.webp?w=520&format=webp";
import whyChooseUsImageMobile from "@/assets/pricing/whyChooseUs-mobile.webp?w=320&format=webp";

import SEO from "../components/SEO";
import axios from "axios";
import { calculatePerEmployeePrice } from "../utils/pricing";

import { lazy, Suspense } from "react";

const CustomerReviews = lazy(
  () => import("../components/payrolewebapp/CustomerReviews"),
);

const AppDownloadCard = lazy(
  () => import("../components/common/AppDownloadCard"),
);

const PayrollBanner = lazy(() => import("../components/common/PayrollBanner"));

const ModernizeHR = lazy(() => import("../components/common/ModernizeHR"));
import { Link } from "react-router-dom";
import { ImWhatsapp } from "react-icons/im";
import { HiPhone } from "react-icons/hi2";
import MobileAppsSection from "./MobileAppsSection";

// ================================

const API_BASE = import.meta.env.VITE_APIBASE_URL || "";
const RAZORKEY = import.meta.env.VITE_RAZORPAY_KEY_ID || "";

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const items = [
  <>
    Rates listed <strong className="font-medium">do not include</strong>{" "}
    statutory taxes (GST).
  </>,
  <>
    Access to <strong className="font-medium">Waggex </strong>is contingent upon
    a valid platform subscription.
  </>,
  <>
    Please <strong className="font-medium">Contact </strong> our sales team for
    custom enterprise solutions.
  </>,
];

const formatINR = (n) => {
  const num = Math.floor(Number(n || 0)); // remove decimal part

  if (num === 0) return "₹0";

  return "₹" + String(num).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

function Tick() {
  return (
    <span
      className="inline-flex items-center justify-center w-6 h-6 rounded-md border border-[#D1DFEA] backdrop-blur-[15px]
      shadow-[0px_0px_4px_0px_#D1DFEA6B,-3px_-3px_7px_0px_#D1DFEA_inset]"
    >
      <FaCheck className="text-[12px] text-[#10324F]" />
    </span>
  );
}

function UnTick() {
  return (
    <span
      className="inline-flex items-center justify-center w-6 h-6 rounded-md border border-[#D1DFEA] backdrop-blur-[15px]
      shadow-[0px_0px_4px_0px_#D1DFEA6B,-3px_-3px_7px_0px_#D1DFEA_inset]"
    ></span>
  );
}

// ✅ IMPORTANT: must match buildSectionsFromPlans output
const slugify = (name = "") =>
  name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]/g, "");

// =============================

const getSaveAmountPrev = (plan) => {
  // console.log("plan price ======>>>> ", plan);
  if (!plan.priceMonthly || !plan.priceYearly) return 0;
  return plan.priceMonthly * 12 - plan.priceYearly;
};

const getSaveAmount = (plan) => {
  // console.log("plan price ======>>>> ", plan);
  const discountPercent = Number(plan.original?.discountPercent ?? 0);
  const yearlyPrice = Number(plan.priceYearly ?? 0);

  if (!yearlyPrice || discountPercent <= 0 || discountPercent >= 100) {
    return 0;
  }

  const originalPrice = yearlyPrice / (1 - discountPercent / 100);
  const saveAmount = originalPrice - yearlyPrice;

  return Math.round(saveAmount);
};

function PricingCardSkeleton() {
  return (
    <div className="p-6 animate-pulse">
      <div className="h-6 bg-[#8aa0ba] rounded mb-4" />
      <div className="h-10 bg-[#8aa0ba] rounded mb-4" />
      <div className="h-5 bg-[#8aa0ba] rounded mb-6" />
      <div className="h-10 bg-[#8aa0ba] rounded" />
    </div>
  );
}

function PricingSkeleton() {
  return (
    <div className="rounded-xl overflow-hidden border border-[#d9e7f3] bg-[#f6fbff] animate-pulse">
      <div className="grid grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="p-6 border-r border-[#d9e7f3]">
            <div className="h- 5 bg-[#8aa0ba] rounded mb-4" />
            <div className="h-8 bg-[#8aa0ba] rounded mb-4" />
            <div className="h-4 bg-[#8aa0ba] rounded mb-2" />
            <div className="h-4 bg-[#8aa0ba] rounded mb-6" />
            <div className="h-10 bg-[#8aa0ba] rounded" />
          </div>
        ))}
      </div>

      {Array.from({ length: 3 }).map((_, section) => (
        <div key={section} className="border-t border-[#d9e7f3]">
          <div className="h-6 bg-gray-100" />

          {Array.from({ length: 3 }).map((_, row) => (
            <div
              key={row}
              className="grid grid-cols-6 border-t border-[#edf3f8]"
            >
              <div className="p-4">
                <div className="h-4 bg-[#8aa0ba] rounded" />
              </div>

              {Array.from({ length: 5 }).map((_, col) => (
                <div key={col} className="flex justify-center items-center p-4">
                  <div className="w-5 h-5 rounded bg-[#8aa0ba]" />
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
export default function Pricing() {
  //   const [sectionsForPricing, setSectionsForPricing] = useState([]);
  //   const [plans, setPlans] = useState([]);
  const [monthlyPlans, setMonthlyPlans] = useState([]);
  const [yearlyPlans, setYearlyPlans] = useState([]);
  const [billing, setBilling] = useState("Monthly"); // "Monthly" | "Yearly"
  const [openSections, setOpenSections] = useState({});
  const [subscribeLoading, setSubscribeLoading] = useState("");
  const [loadingPlans, setLoadingPlans] = useState(true);

  const fetchPlans = async () => {
    try {
      setLoadingPlans(true);

      const [monthlyRes, yearlyRes] = await Promise.all([
        axios.get(`${API_BASE}/subscription-plans-new?planType=Monthly`),
        axios.get(`${API_BASE}/subscription-plans-new?planType=Yearly`),
      ]);

      setMonthlyPlans(monthlyRes?.data?.data || []);
      setYearlyPlans(yearlyRes?.data?.data || []);
    } catch (error) {
      console.error("Error fetching plans:", error);
    } finally {
      setLoadingPlans(false);
    }
  };
  // ====================

  useEffect(() => {
    if (navigator.userAgent === "ReactSnap") return;
    fetchPlans();
  }, []);
  const planPrices = useMemo(() => {
    const activePlans = billing === "Monthly" ? monthlyPlans : yearlyPlans;

    return activePlans.map((plan) => ({
      key: slugify(plan.planTitle),
      id: plan._id,
      name: plan.planTitle,
      features: plan.features || {}, // ✅ ADD THIS
      planPrice: plan.basePrice,
      price: plan.basePrice,
      basePrice: plan.basePrice,
      maxEmployees: plan.maxEmployees || 10,
      discountPercent: plan.discountPercent || 0,
      save:
        billing === "Yearly"
          ? Math.round(
              (Number(plan.basePrice || 0) *
                Number(plan.discountPercent || 0)) /
                (100 - Number(plan.discountPercent || 0)),
            )
          : 0,
      cta: Number(plan.basePrice) === 0 ? "Start Free" : "Choose Plan",
      original: plan,
    }));
  }, [billing, monthlyPlans, yearlyPlans]);
  const sectionsForPricing = useMemo(() => {
    const activePlans = billing === "Monthly" ? monthlyPlans : yearlyPlans;

    const firstPlan = activePlans?.[0];

    if (!firstPlan?.features) return [];

    return Object.entries(firstPlan.features).map(([title, featureList]) => ({
      title,
      rows: featureList.map((feature) => ({
        label: feature,
      })),
    }));
  }, [billing, monthlyPlans, yearlyPlans]);
  useEffect(() => {
    if (!sectionsForPricing.length) return;

    setOpenSections(
      Object.fromEntries(
        sectionsForPricing.map((s, index) => [s.title, index === 0]),
      ),
    );
  }, [sectionsForPricing]);

  // ==================

  const toggleSection = (title) => {
    setOpenSections((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const subscribePlan = async (planId, userId, planName) => {
    try {
      setSubscribeLoading(true);
      const isLoaded = await loadRazorpayScript();

      if (!isLoaded) {
        alert("Razorpay failed to load");
        setSubscribeLoading(false);
        return;
      }
      const res = await axios.post(
        API_BASE + "/payment/create-subscription-payment",
        { planId, userId },
      );
      if (!res.data.subscriptionId) {
        alert("Subscription not created");
        setSubscribeLoading(false);
        return;
      }
      const options = {
        key: RAZORKEY,
        subscription_id: res.data.subscriptionId,
        name: planName || "Waggex Premium",
        description: "Monthly Subscription",
        handler: async function (response) {
          try {
            const verifyRes = await axios.post(
              API_BASE + "/payment/verify-subscription-payment",
              {
                ...response,
                userId,
                planId,
              },
            );

            if (verifyRes.data.success) {
              window.location.href =
                window.location.origin + "/payment-success";

              localStorage.setItem("new_notification", true);

              // console.log("verifyRes in success ");
            } else {
              window.location.href = window.location.origin + "/payment-cancel";
              localStorage.setItem("new_notification", true);
            }
          } catch (err) {
            console.error("Verification error:", err);
            alert("Error verifying payment. Please contact support.");
          }
        },
        theme: { color: "#10324F" },
      };
      // console.log("subscribePlan test 7");
      //   console.log("options", verifyRes);
      const razorpay = new window.Razorpay(options);
      razorpay.open();

      setSubscribeLoading(false);
      // setLoadingPlanId(null);
    } catch (error) {
      console.error("Error in subscribePlan:", error);
      alert("Something went wrong, please try again.");
      setSubscribeLoading(false);
      // setLoadingPlanId(false);
    }
  };

  const handleChoosePlan = () => {
    window.location.href = "https://app.waggex.com/signup";
  };

  const DISCOUNT_PERCENT = 20;

  function addPercentage(amount, percent) {
    return amount + (amount * percent) / 100;
  }

  return (
    <>
      <SEO
        title="Waggex Payroll Pricing | Free Payroll Plans"
        description="Compare Waggex payroll software pricing with transparent plans. Get free payroll software with attendance, selfie & GPS tracking, tax compliance, and mobile app. No hidden costs."
        keywords="Payroll Software, Payroll Software Pricing, Payroll Software India, Online Payroll Software, Payroll Management Software"
        canonical="https://www.waggex.com/pricing"
      />
      <section className="relative bg-linear-to-b from-[#d3e0eb] to-[#FFFFFF] w-full">
        {/* soft background like screenshot */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 left-1/2 -translate-x-1/2  rounded-full bg-[#dfeaf3] blur-3xl opacity-70" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          {/* ===== HEADER (TOP) ===== */}
          <div className="text-center max-w-5xl mx-auto  pt-12 mb-6 sm:mb-8">
            <h1 className="text-center text-[#10324F] text-xl md:text-3xl font-bold uppercase tracking-wide">
              Simple, Transparent Pricing for Growing Businesses
            </h1>

            <p className="mt-3 feature-sub-title max-w-2xl mx-auto min-h-24 md:min-h-14">
              Flexible plans designed to help businesses manage payroll,
              attendance, HR, and compliance with predictable pricing that
              scales as your team grows
            </p>
          </div>

          <div className="flex justify-center mb-6 sm:mb-8">
            <div className="relative p-1.5 rounded-xl bg-[#e6f0f8] border border-white/70 shadow-[0_10px_26px_rgba(16,50,79,0.10)] backdrop-blur-[10px]">
              {billing !== "Monthly" && (
                <div className="absolute -top-4 -right-6 sm:-top-5 sm:-right-8 bg-[#ff0000] font-roboto text-white text-xs sm:text-sm font-semibold px-6 py-2.5 rounded-xl shadow-lg z-50">
                  20% OFF
                </div>
              )}

              <div className="relative flex">
                <button
                  onClick={() => setBilling("Monthly")}
                  className={`
                    px-8 sm:px-12 py-2.5 rounded-xl transition
                    font-roboto font-semibold text-[15px] sm:text-[14px]
                    leading-[1.8] tracking-[0.04em]
                     cursor-pointer
                    ${
                      billing === "Monthly"
                        ? "bg-[#f6fbff] text-[#10324F] shadow-[0_10px_20px_rgba(16,50,79,0.10)] border border-[#FFF]"
                        : "text-[#10324F]/80 hover:text-[#10324F]"
                    }
                  `}
                >
                  Monthly
                </button>

                <button
                  onClick={() => setBilling("Yearly")}
                  className={`
                    px-8 sm:px-12 py-2.5 rounded-xl transition
                    font-roboto font-semibold text-[15px] sm:text-[14px]
                    leading-[1.8] tracking-[0.04em]
                     cursor-pointer
                    ${
                      billing === "Yearly"
                        ? "bg-[#f6fbff] text-[#10324F] shadow-[0_10px_20px_rgba(16,50,79,0.10)]"
                        : "text-[#10324F]/80 hover:text-[#10324F]"
                    }
                  `}
                >
                  Annually
                </button>
              </div>
            </div>
          </div>

          {/* ---------------- DESKTOP TABLE ---------------- */}
          <div className="hidden md:block min-h-40">
            {loadingPlans ? (
              <PricingSkeleton />
            ) : (
              <>
                <div className="rounded-xl overflow-hidden border border-[#d9e7f3] bg-[#f6fbff] shadow-[0_14px_34px_rgba(16,50,79,0.10)]">
                  {/* Header row */}
                  <div
                    className={`grid ${billing === "Monthly" ? "grid-cols-6" : "grid-cols-5"}`}
                  >
                    {/* Left intro */}
                    <div className="p-6 bg-[#eaf3fb] border-b md:border-b-0 md:border-r border-[#d9e7f3] text-left">
                      <h2 className="font-roboto font-semibold text-[#10324F] text-[16px] leading-[160%]">
                        Choose plan <br />
                        according to your <br />
                        company size
                      </h2>
                      <div className="mt-3 font-roboto font-normal text-[#4d6b83] text-[14px] leading-[1.8]">
                        <div>One Price.</div>
                        <div>Every Feature Included.</div>
                      </div>
                    </div>

                    {/* Plans */}

                    {/* ====== */}
                    {/* {console.log("planPrices", planPrices)} */}

                    {loadingPlans
                      ? Array.from({ length: 5 }).map((_, i) => (
                          <PricingCardSkeleton key={i} />
                        ))
                      : planPrices.map((p) => {
                          const isYearly = billing !== "Monthly";
                          const isFree = p.name === "Free Starter";
                          const discountPercent = Number(
                            p.discountPercent ?? 20,
                          );

                          const originalPrice =
                            isYearly && !isFree && discountPercent > 0
                              ? Math.round(
                                  p.price / (1 - discountPercent / 100),
                                )
                              : null;

                          // console.log("p of planPrice", p);
                          return (
                            <div
                              key={p.key}
                              className="
            relative p-4 sm:p-6
            border-r last:border-r-0 border-[#d9e7f3]
            text-center bg-[#f6fbff]
            flex flex-col
          "
                            >
                              {/* Plan name */}
                              <div className="font-roboto font-medium text-[#10324F] text-[14px] sm:text-[16px] md:text-[18px] leading-[1.6] tracking-[0.02em]">
                                {p.name}
                              </div>

                              {/* Ribbon */}
                              {isYearly && !isFree && discountPercent > 0 && (
                                <div className="al_waggex_price_ribbon">
                                  {discountPercent}% Off
                                </div>
                              )}

                              {/* Price block */}
                              <div className="mt-3 flex flex-col items-center">
                                {/* Original price amount, percent*/}
                                {isYearly && !isFree && originalPrice && (
                                  <span className="font-roboto text-[#10324F] text-[12px] sm:text-[13px] line-through">
                                    {/* {addPercentage(
                              p.basePrice,
                              p.original.discountPercent,
                            )} */}
                                    {formatINR(
                                      addPercentage(
                                        p.basePrice,
                                        p.original.discountPercent,
                                      ),
                                    )}
                                  </span>
                                )}

                                {/* Current price */}
                                <span className="font-roboto font-semibold text-[#10324F] text-[20px] sm:text-[24px] md:text-[26px] leading-tight">
                                  {formatINR(p.price)}
                                </span>
                              </div>

                              {/* Sub text */}
                              <div className="mt-2 font-roboto text-[#10324F] text-[11px] sm:text-xs leading-[1.6] whitespace-pre-line min-h-10.5">
                                {billing === "Monthly" ? (
                                  <>
                                    {calculatePerEmployeePrice(
                                      p.planPrice,
                                      p.maxEmployees,
                                    )}
                                    {`/per employee\nUp to ${p.maxEmployees} Employees`}
                                  </>
                                ) : (
                                  <>
                                    {p.save > 0 && (
                                      <div className="text-[11px] sm:text-xs text-[#10324F]">
                                        Save {formatINR(p.save)}
                                      </div>
                                    )}
                                    {`Up to ${p.maxEmployees} Employees`}
                                  </>
                                )}
                              </div>

                              {/* Button pinned to bottom */}
                              <button
                                onClick={() => handleChoosePlan(p)}
                                className="
              mt-auto
              w-full
              text-[#10324F]
              text-[12px] sm:text-[13px]
              font-medium
              border border-[#d9e7f3]
              rounded-md
              py-2.5
              shadow-[0_6px_16px_rgba(16,50,79,0.08)]
              hover:shadow-[0_10px_22px_rgba(16,50,79,0.12)]
              transition
              bg-white/40
              cursor-pointer
            "
                              >
                                {p.cta}
                              </button>
                            </div>
                          );
                        })}
                  </div>

                  {/* Feature sections */}

                  {/* {console.log("sectionsForPricing", sectionsForPricing)} */}
                  {sectionsForPricing.map((section) => {
                    const open = openSections[section.title];

                    return (
                      <div
                        key={section.title}
                        className="border-t border-[#d9e7f3]"
                      >
                        {/* section header */}
                        <button
                          onClick={() => toggleSection(section.title)}
                          className="w-full grid grid-cols-5 items-center 
                      bg-[linear-gradient(180deg,rgba(209,223,234,0.104)_50.18%,rgba(209,223,234,0.4)_100%)]
                      shadow-[-3px_-3px_10px_0px_#D1DFEA_inset] px-6 py-3"
                        >
                          <div className="col-span-6 flex items-center gap-3 justify-start">
                            <span className="font-roboto font-medium text-[#10324F] text-[15px] leading-normal">
                              {section.title}
                            </span>

                            <RiArrowDownSLine
                              className={`text-[#10324F] transition-transform ${
                                open ? "rotate-180" : ""
                              }`}
                            />
                          </div>
                        </button>

                        {/* rows */}
                        <div
                          className={`transition-all duration-300 ease-in-out ${
                            open ? "max-h-250 opacity-100" : "max-h-0"
                          } overflow-hidden`}
                        >
                          {section.rows.map((row, idx) => (
                            <div
                              key={row.label}
                              className={`grid ${
                                billing === "Monthly"
                                  ? "grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr]"
                                  : "grid-cols-[1fr_1fr_1fr_1fr_1fr]"
                              } ${
                                idx !== section.rows.length - 1
                                  ? "border-b border-[#edf3f8]"
                                  : ""
                              }`}
                            >
                              <div className="p-4 px-6 bg-white/40 border-r border-[#edf3f8] font-roboto font-normal text-[#10324F] text-[12px] leading-normal">
                                {row.label}
                              </div>

                              {planPrices.map((p) => (
                                <div
                                  key={p.key}
                                  className="p-4 flex items-center justify-center border-r last:border-r-0 border-[#edf3f8] bg-white/40"
                                >
                                  {/* ✅ FIX: compare slug keys */}
                                  <Tick />
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          {/* ---------------- MOBILE / TABLET CARDS ---------------- */}
          <div className="md:hidden min-h-280">
            {loadingPlans ? (
              <div className="p-4 space-y-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-[#d9e7f3] bg-white p-4 animate-pulse"
                  >
                    <div className="h-5 bg-[#8aa0ba] rounded w-32 mb-3" />
                    <div className="h-8 bg-[#8aa0ba] rounded w-24 mb-3" />
                    <div className="h-4 bg-[#8aa0ba] rounded w-full mb-2" />
                    <div className="h-4 bg-[#8aa0ba] rounded w-2/3 mb-4" />
                    <div className="h-10 bg-[#8aa0ba] rounded" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-[#d9e7f3] bg-[#f6fbff] shadow-[0_14px_34px_rgba(16,50,79,0.10)] overflow-hidden">
                <div className="px-4 py-4 bg-[#eaf3fb]">
                  <div className="text-[#10324F] font-semibold text-[14px] leading-snug">
                    Choose plan according to your company size
                  </div>
                  <div className="mt-2 text-[#4d6b83] text-[12px]">
                    One Price. Every Feature Included.
                  </div>
                </div>

                <div className="p-4 space-y-4">
                  {planPrices.map((p) => {
                    const isYearly = billing !== "Monthly";
                    const isFree = p.name === "Free Starter";
                    const discountPercent = Number(p.discountPercent ?? 20);
                    const originalPrice =
                      isYearly && !isFree && discountPercent > 0
                        ? Math.round(p.price / (1 - discountPercent / 100))
                        : null;

                    return (
                      <div
                        key={p.key}
                        className="rounded-xl border border-[#d9e7f3] bg-white backdrop-blur-[10px]  overflow-hidden"
                      >
                        <div className="px-4 py-4 flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            {/* Ribbon */}
                            {isYearly && !isFree && discountPercent > 0 && (
                              <div className="al_waggex_price_ribbon">
                                {discountPercent}% Off
                              </div>
                            )}

                            {/* Plan name */}
                            <div className="text-[#10324F] font-semibold text-[14px] truncate">
                              {p.name}
                            </div>

                            {/* Price block */}
                            <div className="mt-1 flex items-end gap-2">
                              {/* Strikethrough original price */}
                              {isYearly && !isFree && originalPrice && (
                                <span className="text-[#10324F] text-[12px] line-through">
                                  {formatINR(
                                    addPercentage(
                                      p?.basePrice,
                                      p?.original?.discountPercent,
                                    ),
                                  )}
                                </span>
                              )}

                              {/* Current price */}
                              <span className="text-[#10324F] font-bold text-[18px]">
                                {formatINR(p.price)}
                              </span>
                            </div>

                            {/* Sub info */}
                            <div className="mt-2 text-[#10324F] text-[11px] whitespace-pre-line leading-5">
                              {billing === "Monthly" ? (
                                <>
                                  {calculatePerEmployeePrice(
                                    p.planPrice,
                                    p.maxEmployees,
                                  )}{" "}
                                  {` per Employee\nUp to ${p.maxEmployees} Employees`}
                                </>
                              ) : (
                                <>
                                  {/* Save line (optional) */}
                                  {p.save > 0 && (
                                    <div className="mt-1 text-[12px] text-[#10324F]">
                                      Save {formatINR(p.save)}
                                    </div>
                                  )}
                                  {`Up to ${p.maxEmployees} Employees`}
                                </>
                              )}
                            </div>
                          </div>

                          {/* CTA */}
                          <button
                            onClick={() => handleChoosePlan(p)}
                            className="
            shrink-0 mt-1
            text-[#10324F] text-[12px] font-medium
            bg-white/70
            border border-[#d9e7f3]
            rounded-md
            px-4 py-2.5
            shadow-[0_6px_16px_rgba(16,50,79,0.08)]
            active:scale-[0.99]
            transition
            cursor-pointer
          "
                          >
                            {p.cta || "Choose Plan"}
                          </button>
                        </div>

                        {/* per-plan features accordion */}
                        <div className="border-t border-[#edf3f8]">
                          {sectionsForPricing.map((section) => (
                            <details key={section.title} className="group">
                              <summary className="list-none cursor-pointer px-4 py-3 bg-[#f3f9ff] flex items-center justify-between">
                                <span className="text-[#10324F] text-[13px] font-semibold">
                                  {section.title}
                                </span>
                                <RiArrowDownSLine className="text-[#10324F] transition" />
                              </summary>

                              <div className="px-4 py-3 space-y-2 bg-white/50">
                                {section.rows.map((row) => {
                                  return (
                                    <div
                                      key={row.label}
                                      className="flex items-start justify-between gap-3"
                                    >
                                      <div className="text-[#10324F] text-[12px] leading-5">
                                        {row.label}
                                      </div>

                                      <Tick />
                                    </div>
                                  );
                                })}
                              </div>
                            </details>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <MobileAppsSection />
        </div>
      </section>

      <section className="relative max-w-6xl mx-auto px-4 py-10 min-h-70 md:min-h-30">
        <div
          className="
        w-full
        px-8 py-8 "
        >
          <ul className="space-y-3 sm:space-y-4">
            {items.map((text, idx) => (
              <li key={idx} className="flex items-start gap-3 font-roboto">
                {/* Diamond Icon */}

                <span
                  className="
    mt-1.25
    inline-flex
    h-4 w-4 sm:h-4.5 sm:w-4.5
    shrink-0
    items-center
    justify-center
  "
                >
                  <span
                    className="
      w-full h-full
      rotate-45
      rounded-sm
      bg-[linear-gradient(135deg,#8ea2b3_0%,#cfd9e2_100%)]
      shadow-[0_4px_10px_rgba(16,50,79,0.16)]
    "
                  />
                </span>

                {/* Text */}
                <p
                  className="
                font-roboto
    font-light
    text-[#10324F]

    text-[13px] sm:text-[14px]
    leading-[1.8]

    text-justify
              "
                >
                  {text}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>
      <section className="w-full bg-[#F6F9FB] px-6 py-10 min-h-60">
        <div className="max-w-4xl mx-auto text-center">
          {/* Heading */}

          <h2
            className="
            text-[#10324F] font-roboto font-bold uppercase
            text-xl sm:text-[28px]
          "
          >
            Questions? Our team is available 7 days a week
          </h2>

          {/* Description */}

          <p
            className="
            text-[#10324F] mt-4
            text-sm sm:text-base md:text-lg
            max-w-2xl mx-auto
            leading-relaxed
          "
          >
            Support Hours: Mon–Sun, 08:00–20:00
          </p>

          {/* Buttons */}
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact-us"
              className="
                inline-flex items-center justify-center gap-3
                min-w-52.5
                rounded-xl
                px-6 py-3
                font-roboto
                font-medium
                text-[13px] sm:text-[14px]
                bg-[#10324F]
                text-white
               border border-[#AEAEC033]
              "
            >
              <HiPhone className="text-[20px]" />
              Give Us a Call
            </Link>

            {/* bg-[#f3f8fc] */}
            <Link
              to="https://api.whatsapp.com/message/R5IO72F6ZSQRE1?autoload=1&app_absent=0"
              className="
                inline-flex items-center justify-center gap-3
                min-w-52.5
                rounded-xl
                px-6 py-3
                font-roboto
                font-medium
                text-[13px] sm:text-[14px]
                text-[#10324F]
               
              border border-[#AEAEC033]
                shadow-[-6px_-6px_12px_rgba(255,255,255,0.70)_inset,0_12px_24px_rgba(16,50,79,0.10)]
                transition
                hover:shadow-[-6px_-6px_12px_rgba(255,255,255,0.70)_inset,0_16px_30px_rgba(16,50,79,0.14)]
                active:scale-[0.99]
                backdrop-blur-[30px]
              "
            >
              <ImWhatsapp className="text-[20px]" />
              WhatsApp Us
            </Link>
          </div>
        </div>
      </section>
      <section className="bg-[#FFFFFF] w-full py-10 px-6 min-h-100">
        {/* Row 2 */}
        <div className="max-w-6xl mx-auto px-4 ">
          <div className="flex justify-center">
            <h2 className="text-[#10324F] font-roboto font-bold uppercase text-xl sm:text-[28px]">
              Why choose us
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="order-1 md:order-2 space-y-4">
              <div className="payroll-section-content-width">
                <ul className="space-y-3 mt-3">
                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-6.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Complete Access Across Every Plan
                    </span>
                  </li>
                  <li className="flex gap-3  items-start">
                    <div className="shrink-0 w-6.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Simple Pricing Without Hidden Costs
                    </span>
                  </li>

                  <li className="flex gap-3  items-start">
                    <div className="shrink-0 w-6.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Designed for Indian Compliance
                    </span>
                  </li>
                  <li className="flex gap-3  items-start">
                    <div className="shrink-0 w-6.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Scales as Your Business Grows
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="order-2 md:order-2 flex justify-center">
              <div className="relative w-full block md:hidden">
                <img
                  src={whyChooseUsImageMobile}
                  width="332"
                  height="211"
                  alt="whyChooseUsImage"
                  fetchPriority="high"
                  className="w-full h-auto object-contain"
                />
              </div>
              <div className="relative w-full aspect-544/345 hidden md:block">
                <img
                  src={whyChooseUsImage}
                  width="544"
                  height="345"
                  alt="whyChooseUsImage"
                  fetchPriority="high"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <Suspense fallback={null}>
        <CustomerReviews />
        {/* <HomeFaq faq1={faq1} faq2={faq2} source="home" /> */}
        <PayrollBanner
          heading="Just Need HRMS Software? We Have A Dedicated Page"
          description="Waggex HRMS Software helps you manage payroll, attendance, leaves, employees, shifts, and payslips from one simple platform."
        />
        <AppDownloadCard />
        <ModernizeHR />
      </Suspense>
    </>
  );
}
